import type { ComponentType } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import type { ChromeVariant } from '@eis/theme';
import { FeatherIcon } from '@eis/patterns';
import { FEATHER_PREFIX, featherToMui } from './feather-icon-map';

/** What the shell needs of an icon: a component that takes MUI's `fontSize`. */
export type NavIconComponent = ComponentType<{
  readonly fontSize?: 'inherit' | 'small' | 'medium' | 'large';
}>;

/**
 * Manifests name icons as strings so a module never has to import an icon component into its
 * manifest (which would drag MUI into the shell's critical path for every module). The shell
 * resolves them here.
 *
 * Two forms (SDD-012, U-4): a MUI component name (`HandshakeIcon`) and a Feather glyph
 * (`feather:briefcase`). In the `suite` variant a Feather name renders the legacy's icon font
 * through `<FeatherIcon>`; in `berry` it resolves to the MUI SVG `feather-icon-map.ts` pairs it
 * with. Unknown names still fall back to `FolderIcon`, in both variants.
 */
const ICONS: Readonly<Record<string, SvgIconComponent>> = {
  HandshakeIcon,
  Inventory2Icon,
  AccountTreeIcon,
  ReceiptIcon,
  SettingsIcon,
  PeopleIcon,
  GavelIcon,
  DescriptionOutlinedIcon,
  FolderIcon,
  ArrowBackIcon,
  BarChartOutlinedIcon,
  StorageOutlinedIcon,
};

/**
 * One component per glyph, created once and cached: a component type created during render
 * remounts its subtree on every pass, which is the reason `AppNav` resolves `HomeIcon` at module
 * scope today.
 */
const featherComponents = new Map<string, NavIconComponent>();

function featherComponent(glyph: string): NavIconComponent {
  const cached = featherComponents.get(glyph);
  if (cached !== undefined) return cached;
  const Component: NavIconComponent = ({ fontSize }) => (
    <FeatherIcon glyph={glyph} fontSize={fontSize ?? 'small'} />
  );
  Component.displayName = `FeatherIcon(${glyph})`;
  featherComponents.set(glyph, Component);
  return Component;
}

function muiIcon(name: string): SvgIconComponent | undefined {
  // `icon-map.json` names some components without the `Icon` suffix (`ArrowBack`); accept both.
  return ICONS[name] ?? ICONS[`${name}Icon`];
}

export function resolveIcon(name: string, variant: ChromeVariant = 'berry'): NavIconComponent {
  if (name.startsWith(FEATHER_PREFIX)) {
    const glyph = name.slice(FEATHER_PREFIX.length);
    if (variant === 'suite') return featherComponent(glyph);
    const mapped = featherToMui[glyph];
    return (mapped === undefined ? undefined : muiIcon(mapped)) ?? FolderIcon;
  }
  return muiIcon(name) ?? FolderIcon;
}
